import { defineComponent as _defineComponent } from 'vue'
import ShiftFilter from '@/components/ShiftFilter.vue';
import TimeSpanFilter from '@/components/TimeSpanFilter.vue';
import WidgetDefinition from '@/models/Charts/widgetDefinition';
import { Device } from '@/models/device';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { MachineType } from '@/models/enums/MachineType';
import { TargetsMetric } from '@/models/enums/TargetsMetric';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import {
  Aggregate,
  arrayToNumber,
  numberToArray,
} from '@/models/metrics/aggregates';
import { getWidgetMachineType } from '@/models/widgetMachineType';
import { devicesService } from '@/services/devices.service';
import { shiftsService } from '@/services/shifts.service';
import { translateFilterTimeSpanEnum } from '@/utils/i18n';
import { isEmpty, isNil } from '@/utils/misc';
import moment from 'moment';
import ThreeDotsOptions from '@/components/Charts/ThreeDotsOptions.vue';
import NewFeatureBadge from '@/components/NewFeatureBadge.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from '@/composables/i18n';
import { currentUser } from '@/utils/permissionUtils';
import QuickSearchItem from '@/models/metrics/quickSearchItem';
import { getTargets } from './targets';


/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'WidgetCard',
  props: {
    widgetDefinition: null,
    isWidgetSelector: { type: Boolean },
    selfManagedFilters: { type: Boolean },
    paramValues: null,
    isFetchingForTheFirstTime: { type: Boolean },
    loading: { type: Boolean },
    threeDotsInnerPosition: { default: 'is-left' }
  },
  emits: ["change", "param-update", "quick-search-select", "expand", "download-data"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'change', value: WidgetDefinition): void;
  (e: 'param-update', value: { [key: string]: any }): void;
  (e: 'quick-search-select', value: QuickSearchItem): void;
  (e: 'expand' | 'download-data'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { widgetDefinition: WidgetDefinition, isWidgetSelector: boolean, selfManagedFilters: boolean, paramValues: Record<string, any>, isFetchingForTheFirstTime: boolean, loading: boolean, threeDotsInnerPosition: string };





const { t } = useI18n();
const datepickerRef = ref<any>(null);
const datepickerDates = ref<Date[]>([]);

onMounted(() => {
  if (Array.isArray(props.widgetDefinition.timeFilter)) {
    datepickerDates.value = props.widgetDefinition.timeFilter.map(
      (x: string) => new Date(x),
    );
  }
});

function onChangeDatepicker(payload: Date[]): void {
  const dates = payload.map((x: Date) =>
    moment(x).subtract(x.getTimezoneOffset(), 'minutes').format('YYYY-MM-DD'),
  ) as [string, string];
  const wd = props.widgetDefinition.getCopy({ timeFilter: dates });
  emit('change', wd);
}

function onChangeTimespan(payload: FilterTimeSpanEnum): void {
  const wd = props.widgetDefinition.getCopy({ timeFilter: payload });
  emit('change', wd);
}

function onChangeShift(payload: number[]): void {
  const wd = props.widgetDefinition.getCopy({ shifts: payload });
  emit('change', wd);
}

function onChangeTarget(target: number[]): void {
  const wd = props.widgetDefinition.getCopy({ target });
  emit('change', wd);
}

function onChangeTimeAxisSpan(axisSpan: FilterTimeAxisSpanEnum): void {
  const wd = props.widgetDefinition.getCopy({ axisSpan });
  emit('change', wd);
}

function onChangeAggregates(payload: Aggregate[]): void {
  const aggregates = arrayToNumber(payload);
  const wd = props.widgetDefinition.getCopy({ aggregates });
  emit('change', wd);
}

function toggleDatepicker() {
  datepickerRef.value?.toggle();
}

function getWidgetTargetMetric(widget: WidgetEnum): TargetsMetric {
  let widgetString = Object.keys(WidgetEnum).find(
    (key: string) => WidgetEnum[key as keyof typeof WidgetEnum] === widget,
  )!;

  // HACK (temporary): We don't want a target for LaserAvailability pie chart,
  // but it will be added in the future.
  if (widgetString !== 'LaserProductivityHist') {
    widgetString = widgetString.replace('Hist', '');
  }

  return widgetString in TargetsMetric
    ? TargetsMetric[widgetString as keyof typeof TargetsMetric]
    : TargetsMetric.None;
}

function getShiftName(id: number): string {
  return shiftsService.store.getNameById(id);
}

const widgetTitle = computed((): string =>
  t(`widget.${props.widgetDefinition.widget}`).toString(),
);

const hasExpandButton = computed((): boolean =>
  props.widgetDefinition.canExpand(),
);

const hasDatepicker = computed(
  (): boolean =>
    props.selfManagedFilters && props.widgetDefinition.hasDatepicker,
);

const hasTimeMenu = computed(
  (): boolean => props.selfManagedFilters && props.widgetDefinition.hasTimeMenu,
);

const hasShiftMenu = computed(
  (): boolean =>
    props.selfManagedFilters && props.widgetDefinition.hasShiftMenu,
);

const hasTarget = computed(
  (): boolean => targetsMetric.value !== TargetsMetric.None,
);

const hasQuickSearch = computed(
  (): boolean => !isNil(props.widgetDefinition.quickSearchFunction),
);

const hasParams = computed(
  (): boolean => Object.keys(props.widgetDefinition.params).length > 0,
);

const hasTagSection = computed((): boolean => {
  if (!props.selfManagedFilters) {
    return false;
  }
  const widgetsWithLabels = [
    WidgetEnum.CameraView,
    WidgetEnum.BendStatus,
    WidgetEnum.CutStatus,
    WidgetEnum.TubeStatus,
  ];
  return (
    hasShiftMenu.value ||
    widgetsWithLabels.includes(props.widgetDefinition.widget)
  );
});

const dateGrouping = computed(
  () => props.widgetDefinition.axisSpan ?? FilterTimeAxisSpanEnum.Day,
);

const aggregates = computed((): Aggregate[] =>
  numberToArray(props.widgetDefinition.aggregates),
);

const targetsMetric = computed((): TargetsMetric | null =>
  !isNil(props.widgetDefinition)
    ? getWidgetTargetMetric(props.widgetDefinition.widget)
    : null,
);

const quickSearchFunction = computed(
  () => props.widgetDefinition.quickSearchFunction,
);

const selectedTimespanTranslated = computed(() =>
  Array.isArray(props.widgetDefinition.timeFilter)
    ? ''
    : translateFilterTimeSpanEnum(props.widgetDefinition.timeFilter),
);

const showThreeDots = computed(
  (): boolean =>
    (props.widgetDefinition.hasDownload ||
      (hasTarget.value && props.selfManagedFilters) ||
      hasQuickSearch.value ||
      (props.widgetDefinition.hasAxisSpanFilter && props.selfManagedFilters) ||
      props.widgetDefinition.hasAggregates ||
      hasParams.value) &&
    !props.isWidgetSelector,
);

const machineType = computed(
  (): MachineType => getWidgetMachineType(props.widgetDefinition.widget),
);

const deviceIds = computed((): string[] => props.widgetDefinition.deviceIds);

const devices = computed((): Device[] => devicesService.store.get());

const subtitle = computed((): string => {
  if (isEmpty(deviceIds.value)) {
    return t('report.overview').toString();
  } else {
    return (
      devices.value.find((dev: Device) => dev.deviceId === deviceIds.value[0])
        ?.name ?? ''
    );
  }
});

const showBigLoader = computed(
  (): boolean =>
    props.isFetchingForTheFirstTime &&
    props.loading &&
    (props.widgetDefinition.isEcharts || props.widgetDefinition.isSlot),
);

const showSmallLoader = computed(
  (): boolean =>
    !props.isFetchingForTheFirstTime &&
    props.loading &&
    (props.widgetDefinition.isEcharts || props.widgetDefinition.isSlot),
);

const isBetaEnabled = computed((): boolean => currentUser().isBetaEnabled);

const timeFilterValue = computed(
  () => props.widgetDefinition.timeFilter as FilterTimeSpanEnum,
);

const targetValues = computed((): number[] => {
  if (isNil(targetsMetric.value)) {
    return [];
  }
  const values = getTargets(
    props.selfManagedFilters,
    props.widgetDefinition.target,
    targetsMetric.value,
    props.widgetDefinition.widget,
  );
  return isNil(values) ? [] : values;
});

return { __sfc: true,props, emit, t, datepickerRef, datepickerDates, onChangeDatepicker, onChangeTimespan, onChangeShift, onChangeTarget, onChangeTimeAxisSpan, onChangeAggregates, toggleDatepicker, getWidgetTargetMetric, getShiftName, widgetTitle, hasExpandButton, hasDatepicker, hasTimeMenu, hasShiftMenu, hasTarget, hasQuickSearch, hasParams, hasTagSection, dateGrouping, aggregates, targetsMetric, quickSearchFunction, selectedTimespanTranslated, showThreeDots, machineType, deviceIds, devices, subtitle, showBigLoader, showSmallLoader, isBetaEnabled, timeFilterValue, targetValues, ShiftFilter, TimeSpanFilter, isEmpty, ThreeDotsOptions, NewFeatureBadge }
}

})